import {
  CONFIG_BIOMETRICS_ENABLE_KEY,
  CONFIG_SMARTHOME_ENABLE_KEY,
  DEVICE_ALERTS_EVENTS,
} from '@/constants/device';
import { get, isEmpty, isString, pick } from 'lodash';

export const CONFIG_SUPPORTED_CATEGORIES_KEY = 'insights_rules.supported_categories';

export const CATEGORIES = { SMART_HOME: 'smart_home', MOTION: 'motion', BIOMETRIC: 'biometric' };

export const CATEGORY_ALERTEVENTTYPE_MAPPING = {
  [CATEGORIES.SMART_HOME]: [DEVICE_ALERTS_EVENTS.INSIGHTS_SMART_HOME_ALERT_EVENT],
  [CATEGORIES.MOTION]: [DEVICE_ALERTS_EVENTS.INSIGHTS_MOTION_ALERT_EVENT],
  [CATEGORIES.BIOMETRIC]: [DEVICE_ALERTS_EVENTS.INSIGHTS_BIOMETRICS_ALERT_EVENT],
};

export const formatEventsForDisplay = (
  events,
  userTimezone,
  locale = 'en-US',
  is24hours = false,
  trans
) => {
  const now = new Date();

  const getDateKey = (date) => {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);

    const obj = {};
    parts.forEach(({ type, value }) => {
      if (type !== 'literal') obj[type] = value;
    });

    return `${obj.year}-${obj.month}-${obj.day}`;
  };

  const getNowInTimezone = () => {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);

    const obj = {};
    parts.forEach(({ type, value }) => {
      if (type !== 'literal') obj[type] = value;
    });

    return obj;
  };

  const getEventPartsInTimezone = (eventDate) => {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(eventDate);

    const obj = {};
    parts.forEach(({ type, value }) => {
      if (type !== 'literal') obj[type] = value;
    });

    return obj;
  };

  const getRelativeTitle = (eventDate) => {
    const nowParts = getNowInTimezone();
    const eventParts = getEventPartsInTimezone(eventDate);

    // Calculate minute diff within the user's timezone
    const nowMinutes = parseInt(nowParts.hour) * 60 + parseInt(nowParts.minute);
    const eventMinutes = parseInt(eventParts.hour) * 60 + parseInt(eventParts.minute);

    const todayKey = getDateKey(now);
    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowKey = getDateKey(tomorrowDate);
    const eventKey = getDateKey(eventDate);

    if (eventKey === todayKey) {
      const diffMinutes = eventMinutes - nowMinutes;
      if (diffMinutes >= -60 && diffMinutes <= 0) {
        return trans('NOW');
      }
      return trans('LATER_TODAY');
    }

    if (eventKey === tomorrowKey) {
      return trans('TOMORROW');
    }

    return new Intl.DateTimeFormat(locale, {
      timeZone: userTimezone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(eventDate);
  };

  const formatTime = (eventDate) => {
    return new Intl.DateTimeFormat(locale, {
      timeZone: userTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: !is24hours,
    }).format(eventDate);
  };

  return [...events]
    .sort((a, b) => new Date(a.event_timestamp).getTime() - new Date(b.event_timestamp).getTime())
    .filter((event) => new Date(event.event_timestamp).getTime() >= now.getTime() - 60 * 60 * 1000)
    .map((event) => {
      const eventDate = new Date(event.event_timestamp);
      return {
        title: getRelativeTitle(eventDate),
        time: formatTime(eventDate),
        category: event.event_name,
        eventType: event.event_type,
        taskCategory: event.category,
      };
    })
    .slice(0, 3);
};

export const alertColumn = (trans) => [
  {
    title: trans('DATE_L'),
    dataIndex: 'date',
    key: 'date',
    render: (date) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#113E57' }}>{date}</div>
        </div>
      </div>
    ),
    width: 120,
  },
  {
    title: '',
    dataIndex: 'time',
    key: 'time',
    render: (time) => <div style={{ fontSize: 14, color: '#808080' }}>{time ?? '-'}</div>,
    width: 180,
  },
  {
    title: trans('CATEGORY'),
    dataIndex: 'category',
    key: 'category',
    render: (val) => (
      <div style={{ fontSize: 16, fontWeight: 900, color: '#113E57' }}>{val ?? '-'}</div>
    ),
    width: 250,
  },
  {
    title: trans('RULE_NAME'),
    dataIndex: 'ruleName',
    key: 'ruleName',
    render: (val) => (
      <div style={{ fontSize: 16, fontWeight: 900, color: '#113E57' }}>{val ?? '-'}</div>
    ),
    width: 360,
  },
  {
    title: trans('LOCATION'),
    dataIndex: 'location',
    key: 'location',
    render: (val) => (
      <div style={{ fontSize: 16, fontWeight: 900, color: '#113E57' }}>{val ?? '-'}</div>
    ),
    width: 150,
  },
];

const getCategoryLabel = (type, trans) => {
  switch (type) {
    case DEVICE_ALERTS_EVENTS.INSIGHTS_MOTION_ALERT_EVENT:
      return trans('MOTION');
    case DEVICE_ALERTS_EVENTS.INSIGHTS_SMART_HOME_ALERT_EVENT:
      return trans('SMART_HOME');
    case DEVICE_ALERTS_EVENTS.INSIGHTS_BIOMETRICS_ALERT_EVENT:
      return trans('BIOMETRICS');
  }
};

const getDatePartsInTZ = (date, timezone, locale, is24hours) => {
  const parts = {};
  const formatter = new Intl.DateTimeFormat(locale || 'en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: !is24hours,
  });
  formatter.formatToParts(date).forEach(({ type, value }) => {
    parts[type] = value;
  });
  return parts;
};

const normalizeMacAddress = (mac) => (typeof mac === 'string' ? mac.trim().toUpperCase() : '');

const getAccessoryLocationByMac = (userAccessories = []) => {
  return userAccessories.reduce((acc, accessory) => {
    const macAddress = normalizeMacAddress(accessory?.basemacaddress);
    const location = accessory?.location;
    const deviceType = accessory?.devicetype;
    if (macAddress && location) {
      acc[macAddress] = {
        location,
        deviceType,
      };
    }
    return acc;
  }, {});
};

const getLocationFromTrigger = (item, accessoryLocationByMac, translateLocation) => {
  const triggerRaw = item?.triggers;
  if (!triggerRaw) {
    return null;
  }
  let parsedTriggers = triggerRaw;
  if (typeof triggerRaw === 'string') {
    try {
      parsedTriggers = JSON.parse(triggerRaw);
    } catch (error) {
      return null;
    }
  }
  if (!Array.isArray(parsedTriggers) || parsedTriggers.length === 0) {
    return null;
  }
  const firstTriggerEvent = parsedTriggers?.[0]?.trigger?.event;
  const messageType = firstTriggerEvent?.message_type;
  const eventPayload =
    typeof messageType === 'string' && messageType ? firstTriggerEvent?.[messageType] : undefined;

  const macAddressRaw = eventPayload?.mac_address;
  if (typeof macAddressRaw !== 'string') {
    return null;
  }
  const macAddresses = macAddressRaw
    .split(',')
    .map((mac) => normalizeMacAddress(mac))
    .filter(Boolean);
  if (macAddresses.length !== 1) {
    return '-';
  }
  const mappedAccessory = accessoryLocationByMac[macAddresses[0]];
  if (!mappedAccessory?.location) {
    return '-';
  }

  if (typeof translateLocation === 'function') {
    return (
      translateLocation(mappedAccessory.location, mappedAccessory.deviceType) ||
      mappedAccessory.location ||
      '-'
    );
  }

  return mappedAccessory.location || '-';
};

export const USER_STATE_LIST = {
  userstates: [
    'insights_biometrics_alert_event',
    'insights_smart_home_alert_event',
    'insights_motion_alert_event',
  ],
};

export const formatTableData = (
  apiData,
  timezone,
  locale,
  trans,
  is24hours,
  userAccessories = [],
  translateLocation
) => {
  const accessoryLocationByMac = getAccessoryLocationByMac(userAccessories);

  // Get "today" and "yesterday" boundaries in the target timezone
  const nowParts = getDatePartsInTZ(new Date(), timezone, locale, is24hours);
  const todayKey = `${nowParts.year}-${nowParts.month}-${nowParts.day}`;

  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayParts = getDatePartsInTZ(yesterdayDate, timezone, locale, is24hours);
  const yesterdayKey = `${yesterdayParts.year}-${yesterdayParts.month}-${yesterdayParts.day}`;

  return [...(apiData || [])]
    .sort((a, b) => new Date(b.utc_timestamp) - new Date(a.utc_timestamp))
    .map((item) => {
      const date = new Date(item?.utc_timestamp);

      if (isNaN(date.getTime())) {
        const triggerLocation = getLocationFromTrigger(
          item,
          accessoryLocationByMac,
          translateLocation
        );
        // Handle genuinely unparseable timestamps
        return {
          key: item.message_id,
          type: item.message_type,
          utc: item?.utc_timestamp || null,
          date: '-',
          time: '-',
          category: getCategoryLabel(item.message_type, trans) || '-',
          ruleName: item?.display_text || '-',
          location: triggerLocation ?? '-',
        };
      }

      const dateParts = getDatePartsInTZ(date, timezone, locale, is24hours);
      const dateKey = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;

      const isToday = dateKey === todayKey;
      const isYesterday = dateKey === yesterdayKey;

      const dateLabel = isToday
        ? trans('TODAY')
        : isYesterday
          ? trans('YESTERDAY')
          : date.toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            timeZone: timezone,
          });

      const timeLabel = date.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: !is24hours,
        timeZone: timezone,
      });

      const locationFromTriggers = getLocationFromTrigger(
        item,
        accessoryLocationByMac,
        translateLocation
      );

      return {
        key: item.message_id,
        type: item.message_type,
        utc: item?.utc_timestamp || null,
        date: dateLabel,
        time: timeLabel,
        category: getCategoryLabel(item.message_type, trans) || '-',
        ruleName: item?.display_text || '-',
        location: locationFromTriggers ?? item.location ?? '-',
      };
    });
};

export const getSupportedCategoriesFromConfig = (currentUserConfig) => {
  const categories = get(currentUserConfig, CONFIG_SUPPORTED_CATEGORIES_KEY, '');
  const isSmartHomeEnabled = get(currentUserConfig, CONFIG_SMARTHOME_ENABLE_KEY, true);
  const isBiometricsEnabled = get(currentUserConfig, CONFIG_BIOMETRICS_ENABLE_KEY, true);
  const rulesCategories =
    isString(categories) && categories.trim() !== '' ? categories.trim().split(',') : [];
  return Object.values(CATEGORIES).filter((category) => {
    if (!isSmartHomeEnabled && category === CATEGORIES.SMART_HOME) return false;
    if (!isBiometricsEnabled && category === CATEGORIES.BIOMETRIC) return false;
    if (isEmpty(rulesCategories)) return false;
    return rulesCategories.includes(category);
  });
};

export const getSupportedAlertEventTypesForCategory = (categories) => {
  if (isEmpty(categories)) return [];
  return Object.values(pick(CATEGORY_ALERTEVENTTYPE_MAPPING, categories)).flat();
};
